import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { X } from 'lucide-react';
import { Habit, HabitType } from '../../../types';
import { useHabitContext } from '../../../context/HabitContext';
import Button from '../Button';
import Modal from '../Modal';
import ErrorBanner from '../ErrorBanner';
import HabitFormFields from './HabitFormFields';
import { useToast } from '../Toast';
import './HabitModals.scss';

interface EditHabitPopoverProps {
  habit?: Habit;
  habits?: Habit[];
  onClose: () => void;
}

const EditHabitPopover: React.FC<EditHabitPopoverProps> = ({
  habit,
  habits,
  onClose,
}) => {
  const { formatMessage: t } = useIntl();
  const {
    categories,
    renameHabit,
    updateHabitType,
    updateHabitColor,
    updateHabitScheduledTime,
    removeHabit,
  } = useHabitContext();
  const { showToast } = useToast();
  const showSelector = Boolean(habits && habits.length > 0);
  const selectableHabits = (habits ?? (habit ? [habit] : [])).filter((h) =>
    categories.some((c) => c.habits.some((item) => item.id === h.id)),
  );
  const [selectedId, setSelectedId] = useState(habit?.id ?? '');
  const current =
    selectableHabits.find((h) => h.id === selectedId) ?? selectableHabits[0];

  const currentId = current?.id;
  const currentName = current?.name;
  const currentColor = current?.color;
  const currentType = current?.type;
  const currentScheduledTime = current?.scheduledTime;

  const [name, setName] = useState(currentName ?? '');
  const [color, setColor] = useState(currentColor ?? '#C25E45');
  const [type, setType] = useState<HabitType>(currentType ?? 'checkbox');
  const [scheduledTime, setScheduledTime] = useState(currentScheduledTime ?? '');
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentId || currentName === undefined || currentColor === undefined || currentType === undefined) {
      return;
    }
    setName(currentName);
    setColor(currentColor);
    setType(currentType);
    setScheduledTime(currentScheduledTime ?? '');
    setError(null);
    setConfirmingDelete(false);
  }, [currentId, currentName, currentColor, currentType, currentScheduledTime]);

  const save = async () => {
    if (timePickerOpen) return;
    if (!current || saving) return;
    const trimmed = name.trim();
    if (!trimmed) return;

    const duplicate = categories
      .flatMap((c) => c.habits)
      .some(
        (h) =>
          h.id !== current.id &&
          h.name.trim().toLowerCase() === trimmed.toLowerCase(),
      );
    if (duplicate) {
      setError(t({ id: 'habit.error.duplicateName' }));
      return;
    }

    setSaving(true);
    try {
      if (trimmed !== current.name) await renameHabit(current.id, trimmed);
      if (type !== current.type) await updateHabitType(current.id, type);
      if (color !== current.color) await updateHabitColor(current.id, color);
      const newTime = scheduledTime || undefined;
      if (newTime !== current.scheduledTime) await updateHabitScheduledTime(current.id, newTime);
      onClose();
    } catch {
      showToast(t({ id: 'toast.habit.saveFailed' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = () => {
    if (!current) return;
    const remaining = selectableHabits.filter((h) => h.id !== current.id);
    removeHabit(current.id);
    setConfirmingDelete(false);
    showToast(t({ id: 'toast.habit.deleted' }));
    if (remaining.length > 0) {
      setSelectedId(remaining[0].id);
    } else {
      onClose();
    }
  };

  const footer = (
    <div className="col-edit-actions">
      <Button variant="primary" size="md" onClick={() => void save()} disabled={!current || timePickerOpen} loading={saving}>
        {t({ id: 'monthly.popover.save' })}
      </Button>
      <Button
        variant="danger"
        size="md"
        icon={<X size={12} />}
        disabled={!current}
        onClick={() => setConfirmingDelete(true)}
      >
        {t({ id: 'monthly.popover.deleteHabit' })}
      </Button>
    </div>
  );

  return (
    <>
    <Modal
      title={t({ id: 'monthly.popover.editTitle' })}
      onClose={onClose}
      maxWidth={420}
      panelClassName="habit-modal"
      footer={footer}
    >
      <div className="habit-modal-body">
        {error && <ErrorBanner message={error} />}
        {current && (
          <>
            {showSelector && selectableHabits.length > 0 && (
              <div className="col-edit-section">
                <span className="col-edit-label">{t({ id: 'daily.habit.select' })}</span>
                <select
                  className="col-edit-input"
                  value={current.id}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  {selectableHabits.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <HabitFormFields
              name={name}
              onNameChange={(value) => {
                setName(value);
                if (error) setError(null);
              }}
              color={color}
              onColorChange={setColor}
              type={type}
              onTypeChange={setType}
              scheduledTime={scheduledTime}
              onScheduledTimeChange={setScheduledTime}
              onTimePickerOpenChange={setTimePickerOpen}
              autoFocusName={!showSelector}
              onNameKeyDown={(e) => {
                if (e.key === 'Enter') void save();
                if (e.key === 'Escape') onClose();
              }}
            />
          </>
        )}
      </div>
    </Modal>
    {confirmingDelete && current && (
      <Modal
        title={t({ id: 'habit.delete.confirmTitle' })}
        onClose={() => setConfirmingDelete(false)}
        maxWidth={400}
        overlayClassName="modal-overlay--stacked"
        panelClassName="habit-modal"
        footer={
          <div className="col-edit-actions">
            <Button variant="secondary" size="md" onClick={() => setConfirmingDelete(false)}>
              {t({ id: 'common.cancel' })}
            </Button>
            <Button variant="danger" size="md" onClick={confirmDelete}>
              {t({ id: 'habit.delete.confirmAction' })}
            </Button>
          </div>
        }
      >
        <div className="habit-modal-body">
          <p>{t({ id: 'habit.delete.confirm' }, { name: current.name })}</p>
          <p>{t({ id: 'habit.delete.confirmProgress' })}</p>
          {!current.temporary && <p>{t({ id: 'habit.delete.confirmStats' })}</p>}
        </div>
      </Modal>
    )}
    </>
  );
};

export default EditHabitPopover;
